import { useState } from "react";
import { QrCode as QrCodeIcon } from "lucide-react";
import { isTvBrowser } from "../lib/device";
import QrModal from "./QrModal";

/**
 * Universal "Open on Phone" affordance for any external link.
 *
 * On desktop/mobile: renders a tiny QR icon *next* to the primary link,
 *   clicking it opens a QR modal to beam the URL to a phone.
 * On TV browsers: renders ONLY the QR affordance and takes over the primary
 *   click too — because direct clicks on Spotify / Apple Music / JustWatch
 *   fail on Samsung Tizen 4.5 (no Widevine DRM).
 *
 * Usage:
 *   <PhoneQrPopover url="https://open.spotify.com/..." label="Listen on Spotify" tvLabel="Listen">
 *     <a href="..." className="..." data-testid="...">LISTEN</a>
 *   </PhoneQrPopover>
 */
export default function PhoneQrPopover({
  url,
  label,
  tvLabel,
  children,
  qrOnly,
  testId,
}) {
  const [open, setOpen] = useState(false);
  const isTv = qrOnly ?? isTvBrowser();
  const buttonText =
    tvLabel || (typeof label === "string" && label.length <= 22 ? label : "Open on phone");

  return (
    <>
      {!isTv && children}
      <button
        type="button"
        aria-label={`Open on phone — ${label}`}
        title="Open on phone"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen(true);
        }}
        className={
          isTv
            ? "inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-ink-muted hover:text-press-red shrink-0"
            : "inline-flex items-center align-middle ml-1 text-ink-muted hover:text-press-red"
        }
        data-testid={testId || "phone-qr-btn"}
      >
        {isTv ? (
          <>
            <QrCodeIcon size={12} /> {buttonText}
          </>
        ) : (
          <QrCodeIcon size={12} aria-hidden="true" />
        )}
      </button>

      <QrModal url={url} label={label} open={open} onClose={() => setOpen(false)} />
    </>
  );
}
